import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CaretDownOutlined, CaretUpOutlined } from "@ant-design/icons";
import { Alert, Button, Checkbox, Drawer, Spin, Table } from "antd";
import { fetchClientImportGuests, importHealthClients } from "./health-clients-api";
import { formatDate, pick, toNumber } from "./health-clients-utils";

const DRAWER_WIDTH_KEY = "planOnline.health.clients.importDrawer.width";
const FILTERS_STORAGE_KEY = "planOnline.health.clients.importDrawer.filters";
const MIN_WIDTH = 720;
const MAX_WIDTH = 1280;
const DEFAULT_WIDTH = 980;
const TABLE_HEADER_OFFSET = 39;

const clampWidth = (value) => {
  const max = typeof window === "undefined" ? MAX_WIDTH : Math.min(MAX_WIDTH, window.innerWidth);
  const n = Number(value);
  if (!Number.isFinite(n)) return DEFAULT_WIDTH;
  return Math.min(max, Math.max(MIN_WIDTH, Math.round(n)));
};

const loadWidth = () => {
  try {
    return clampWidth(localStorage.getItem(DRAWER_WIDTH_KEY) ?? DEFAULT_WIDTH);
  } catch {
    return DEFAULT_WIDTH;
  }
};

const saveWidth = (value) => {
  try {
    localStorage.setItem(DRAWER_WIDTH_KEY, String(clampWidth(value)));
  } catch {
    /* ignore */
  }
};

const loadOnlyResiding = () => {
  try {
    const raw = localStorage.getItem(FILTERS_STORAGE_KEY);
    if (!raw) return true;
    const parsed = JSON.parse(raw);
    return parsed?.onlyResiding !== false;
  } catch {
    return true;
  }
};

const saveOnlyResiding = (value) => {
  try {
    localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify({ onlyResiding: Boolean(value) }));
  } catch {
    /* ignore */
  }
};

const startOfDay = (value) => {
  if (!value) return null;
  const d = value instanceof Date ? new Date(value) : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  d.setHours(0, 0, 0, 0);
  return d;
};

const isResidingToday = (row) => {
  const today = startOfDay(new Date());
  const beg = startOfDay(row.arrival);
  const end = startOfDay(row.departure);
  if (!today || !beg || !end) return false;
  return today >= beg && today <= end;
};

const sortIcon = ({ sortOrder }) => {
  if (sortOrder === "ascend") return <CaretUpOutlined />;
  if (sortOrder === "descend") return <CaretDownOutlined />;
  return null;
};

const compareText = (a, b) =>
  String(a ?? "").localeCompare(String(b ?? ""), "ru", { numeric: true, sensitivity: "base" });

const compareNumber = (a, b) => {
  const na = toNumber(a);
  const nb = toNumber(b);
  if (na == null && nb == null) return 0;
  if (na == null) return -1;
  if (nb == null) return 1;
  return na - nb;
};

const compareDate = (a, b) => {
  const da = startOfDay(a);
  const db = startOfDay(b);
  if (!da && !db) return 0;
  if (!da) return -1;
  if (!db) return 1;
  return da.getTime() - db.getTime();
};

const withSort = (column, compare) => ({
  ...column,
  sorter: (a, b) => compare(a[column.dataIndex], b[column.dataIndex]),
  sortDirections: ["ascend", "descend"],
  showSorterTooltip: false,
  sortIcon
});

const sexLabel = (value) => {
  const n = toNumber(value);
  if (n === 1) return "М";
  if (n === 0) return "Ж";
  return "";
};

const normalizeGuest = (row) => {
  const extraId = String(pick(row, "extraId", "ExtraId") ?? "").trim();
  return {
    extraId,
    name: pick(row, "name", "Name") ?? "",
    address: pick(row, "address", "Address") ?? "",
    birthDate: pick(row, "birthDate", "BirthDate"),
    sex: toNumber(pick(row, "sex", "Sex")),
    room: pick(row, "room", "Room") ?? "",
    arrival: pick(row, "arrival", "Arrival"),
    departure: pick(row, "departure", "Departure"),
    comment: pick(row, "comment", "Comment") ?? "",
    phone: pick(row, "phone", "Phone") ?? "",
    passport: pick(row, "passport", "Passport") ?? ""
  };
};

const COLUMNS = [
  withSort(
    {
      title: "#",
      dataIndex: "extraId",
      key: "extraId",
      width: 160,
      ellipsis: true
    },
    compareText
  ),
  withSort(
    {
      title: "Гость",
      dataIndex: "name",
      key: "name",
      width: 180,
      ellipsis: true
    },
    compareText
  ),
  withSort(
    {
      title: "Адрес",
      dataIndex: "address",
      key: "address",
      width: 220,
      ellipsis: true
    },
    compareText
  ),
  withSort(
    {
      title: "Д. рожд.",
      dataIndex: "birthDate",
      key: "birthDate",
      width: 96,
      render: formatDate
    },
    compareDate
  ),
  withSort(
    {
      title: "Пол",
      dataIndex: "sex",
      key: "sex",
      width: 48,
      align: "center",
      render: sexLabel
    },
    compareNumber
  ),
  withSort(
    {
      title: "Номер",
      dataIndex: "room",
      key: "room",
      width: 80,
      ellipsis: true
    },
    compareText
  ),
  withSort(
    {
      title: "Заселение",
      dataIndex: "arrival",
      key: "arrival",
      width: 110,
      render: formatDate
    },
    compareDate
  ),
  withSort(
    {
      title: "Выселение",
      dataIndex: "departure",
      key: "departure",
      width: 110,
      render: formatDate
    },
    compareDate
  ),
  withSort(
    {
      title: "Комментарий",
      dataIndex: "comment",
      key: "comment",
      width: 140,
      ellipsis: true
    },
    compareText
  )
];

const ClientImportDrawer = ({ open, onClose, onImported }) => {
  const [size, setSize] = useState(loadWidth);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [onlyResiding, setOnlyResiding] = useState(loadOnlyResiding);
  const [tableScrollY, setTableScrollY] = useState(400);
  const requestIdRef = useRef(0);
  const tableWrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError("");
    setRows([]);
    setSelectedKeys([]);

    fetchClientImportGuests()
      .then((data) => {
        if (requestId !== requestIdRef.current) return;
        const next = (Array.isArray(data) ? data : [])
          .map(normalizeGuest)
          .filter((row) => row.extraId);
        setRows(next);
      })
      .catch((e) => {
        if (requestId !== requestIdRef.current) return;
        setError(e.message || "Не удалось получить список гостей");
      })
      .finally(() => {
        if (requestId === requestIdRef.current) setLoading(false);
      });

    return () => {
      requestIdRef.current += 1;
    };
  }, [open]);

  useEffect(() => {
    const element = tableWrapRef.current;
    if (!open || !element || typeof ResizeObserver === "undefined") return undefined;
    const update = () => {
      setTableScrollY(Math.max(160, element.clientHeight - TABLE_HEADER_OFFSET));
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(element);
    return () => observer.disconnect();
  }, [open, loading, rows.length, error, onlyResiding]);

  const onResize = useCallback((next) => {
    setSize(clampWidth(next));
  }, []);

  const onResizeEnd = useCallback(
    (finalSize) => {
      const next = clampWidth(typeof finalSize === "number" ? finalSize : size);
      setSize(next);
      saveWidth(next);
    },
    [size]
  );

  const visibleRows = useMemo(
    () => (onlyResiding ? rows.filter(isResidingToday) : rows),
    [onlyResiding, rows]
  );

  const visibleKeys = useMemo(
    () => new Set(visibleRows.map((row) => row.extraId)),
    [visibleRows]
  );

  const visibleSelectedKeys = useMemo(
    () => selectedKeys.filter((key) => visibleKeys.has(key)),
    [selectedKeys, visibleKeys]
  );

  const onOnlyResidingChange = (checked) => {
    setOnlyResiding(checked);
    saveOnlyResiding(checked);
  };

  const onLoad = () => {
    if (!visibleSelectedKeys.length || saving) return;
    setSaving(true);
    setError("");
    importHealthClients(visibleSelectedKeys)
      .then((result) => {
        const imported = toNumber(pick(result, "imported", "Imported")) ?? 0;
        onImported?.(imported);
      })
      .catch((e) => {
        setError(e.message || "Не удалось загрузить клиентов");
      })
      .finally(() => setSaving(false));
  };

  const rowSelection = useMemo(
    () => ({
      selectedRowKeys: visibleSelectedKeys,
      onChange: (keys) => setSelectedKeys(keys.map(String)),
      getCheckboxProps: (record) => ({
        name: record.name
      })
    }),
    [visibleSelectedKeys]
  );

  const scrollX = useMemo(
    () => COLUMNS.reduce((sum, col) => sum + (Number(col.width) || 120), 0),
    []
  );

  return (
    <Drawer
      placement="right"
      title="Загрузка из внешней системы"
      size={size}
      maxSize={MAX_WIDTH}
      open={open}
      onClose={onClose}
      destroyOnClose
      rootClassName="health-clients-import"
      maskClosable={!saving}
      keyboard={!saving}
      resizable={{
        onResize,
        onResizeEnd
      }}
      footer={
        <div className="health-clients-import__footer">
          <Button
            type="primary"
            loading={saving}
            disabled={loading || !visibleSelectedKeys.length}
            onClick={onLoad}
          >
            Загрузить
          </Button>
          <Button onClick={onClose}>Отмена</Button>
        </div>
      }
    >
      {error ? <Alert className="health-clients-import__error" type="error" showIcon message={error} /> : null}
      <div className="health-clients-import__filters">
        <Checkbox
          checked={onlyResiding}
          disabled={loading}
          onChange={(e) => onOnlyResidingChange(e.target.checked)}
        >
          Только проживающие
        </Checkbox>
      </div>
      {loading ? (
        <div className="health-clients-import__center">
          <Spin tip="Запрос гостей внешней системы..." />
        </div>
      ) : (
        <div className="health-clients-import__table-wrap" ref={tableWrapRef}>
          <Table
            className="health-clients-import__table"
            size="small"
            rowKey="extraId"
            columns={COLUMNS}
            dataSource={visibleRows}
            pagination={false}
            scroll={{ y: tableScrollY, x: scrollX }}
            rowSelection={rowSelection}
            locale={{ emptyText: "Нет новых гостей для загрузки" }}
          />
        </div>
      )}
    </Drawer>
  );
};

export default ClientImportDrawer;
